import { useMount } from '@tiger/hooks';
import React, { useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import Toast, { ToastProps } from 'components/Toast';
import classlist from 'utils/classlist';
import { countdownFunc } from './helper';
import LoadingItem, { LoadingItemProps } from './LoadingItem';
import './style.scss';

interface CountdownToastProps extends Omit<ToastProps, 'children'> {
    countdown: number;
    children?: React.ReactNode;
    // 是否用toast的形式展示
    isToast: boolean;
    loadingProps?: Omit<LoadingItemProps, 'content'>;
    // 倒计时中的回调
    callback?: () => void;
    // 倒计时结束的回调
    onEnd?: () => void;
}

const Countdown: React.FC<CountdownToastProps> & {
    open: typeof countdownToastOpen;
    loading: typeof countdownToastLoading;
} = ({ countdown, className, children, visible, isToast, loadingProps, onEnd, callback, ...otherProps }) => {
    const [curCountdown, setCountdown] = useState(countdown);
    const [curVisible, setCurVisible] = useState(visible);
    const finalVisible = curVisible && visible && Boolean(curCountdown);
    const finalClassName = classlist('countdown-root', className);
    const isUnmount = useRef(false);

    useMount(() => {
        const timerObj = countdownFunc(
            curCountdown,
            1,
            () => {
                if (!isUnmount.current) {
                    setCountdown(prev => {
                        return prev - 1;
                    });

                    callback?.();
                }
            },
            () => {
                if (!isUnmount.current) {
                    onEnd?.();
                    setCurVisible(false);
                }
            }
        );

        return () => {
            isUnmount.current = true;
            clearTimeout(timerObj.timer);
        };
    });

    return isToast ? (
        <Toast className={finalClassName} visible={finalVisible} {...otherProps}>
            <LoadingItem content={curCountdown} {...loadingProps} />
            {children && <div className="mt-3">{children}</div>}
        </Toast>
    ) : finalVisible ? (
        <div className={finalClassName}>
            <LoadingItem content={curCountdown} {...loadingProps} />
            {children && <div className="mt-3">{children}</div>}
        </div>
    ) : null;
};

let instance: ReturnType<typeof countdownToastOpen> | null = null;

function countdownToastOpen(
    countdown: number,
    content: React.ReactNode,
    otherProps?: Omit<CountdownToastProps, 'countdown' | 'children' | 'visible'>
) {
    let destroyed: boolean = false;
    let withResolve: (value?: PromiseLike<never>) => void = () => {};

    const divElement = document.createElement('div');
    const rootElement = createRoot(divElement);

    document.body.appendChild(divElement);

    function destroy() {
        if (!destroyed) {
            destroyed = true;

            // 移除一个已经mounted的节点, 同时清楚它的事件处理和状态
            rootElement.unmount();

            document.body.removeChild(divElement);
        }
    }

    function close() {
        render(false, () => withResolve());
    }

    function render(visible: boolean, callback?: () => void) {
        const onExited = () => {
            if (!callback) {
                callback = withResolve;
            }

            instance = null;
            callback!();
            destroy();
        };

        if (visible) {
            rootElement.render(
                <Countdown countdown={countdown} visible={visible} onExited={onExited} isToast {...otherProps}>
                    {content}
                </Countdown>
            );
        } else {
            setTimeout(() => {
                if (!destroyed) {
                    onExited();
                }
            }, 1000);
        }
    }

    render(true);

    return {
        close,
        result: new Promise<never>(resolve => {
            withResolve = resolve;
        }),
        render(newContent) {
            content = newContent;

            render(true);
        }
    };
}

function countdownToastLoading(
    visible: boolean,
    config?: {
        countdown: number;
        content: React.ReactNode;
        otherProps?: Omit<CountdownToastProps, 'countdown' | 'children' | 'visible'>;
    }
) {
    let result;

    if (visible && config) {
        const { countdown, content, otherProps } = config;

        if (!instance) {
            instance = countdownToastOpen(countdown, content, otherProps);
        } else {
            instance.render(content);
        }

        result = instance.result;
    }

    if (!visible && instance) {
        result = instance.result;
        instance.close();
        instance = null;
    }

    return result;
}

Countdown.open = countdownToastOpen;
Countdown.loading = countdownToastLoading;

export default Countdown;
